import {getPATDay} from '@account/utils/dates';

// Normalize a ledger date to a UTC start-of-day millisecond stamp (same
// normalization the term list uses). Returns null for missing/invalid input.
const dayMs = date => {
  const d = getPATDay(date);
  return d?.isValid ? d.toMillis() : null;
};

// buildEntitlementBar collapses the grouped ledger feed into a single
// entitlement bar: the CA-level PROVISION window (the whole entitlement) with
// each ALLOCATE carved out as a band and each allocation boundary marked. This
// is the visual "one entitlement, split by allocation events" the timeline
// header renders. Returns {valid:false} when there's no datable provision window
// to anchor against (pre-ledger PAs, or provisions missing valid_from/to).
export const buildEntitlementBar = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return {valid: false};

  // CA-pool provisions only (a workspace-destined PROVISION doesn't belong to
  // this account's entitlement). Span the full provision extent.
  const provisions = rows.filter(
    r => r.operation_type === 'PROVISION' && !r.destination_workspace_id
  );
  const provStarts = provisions
    .map(r => dayMs(r.valid_from))
    .filter(v => v !== null);
  const provEnds = provisions
    .map(r => dayMs(r.valid_to))
    .filter(v => v !== null);
  if (provStarts.length === 0 || provEnds.length === 0) return {valid: false};

  const start = Math.min(...provStarts);
  const end = Math.max(...provEnds);
  const span = end - start;
  if (span <= 0) return {valid: false};

  const frac = ms => Math.min(1, Math.max(0, (ms - start) / span));

  const bands = [];
  const markers = [];
  const seenMarkers = new Set();
  const addMarker = ms => {
    if (ms === null) return;
    const key = String(ms);
    if (seenMarkers.has(key)) return;
    seenMarkers.add(key);
    markers.push({frac: frac(ms), ms});
  };

  for (const r of rows) {
    if (r.operation_type !== 'ALLOCATE') continue;
    const from = dayMs(r.valid_from);
    const to = dayMs(r.valid_to);
    if (from === null || to === null) continue;
    bands.push({
      fromFrac: frac(from),
      toFrac: frac(to),
      workspaceName: r.destination_workspace_name ?? null,
      fromMs: from,
      toMs: to,
    });
    // Only mark boundaries strictly inside the span — the entitlement's own
    // start/end are already labelled at the bar's ends, so a boundary at either
    // edge would just duplicate them (and overflow the container).
    if (from > start && from < end) addMarker(from);
  }

  return {valid: true, start, end, bands, markers};
};
